import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { ArrowRight } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export interface ChartCardProps {
  title: string
  icon?: LucideIcon
  /** Period selector rendered on the trailing edge of the header. */
  filter?: ReactNode
  /** "View X Report →" link pinned to the card footer. */
  footerLink?: { label: string; to: string }
  children: ReactNode
  className?: string
  bodyClassName?: string
}

/**
 * The widget shell used by every dashboard card: icon + title, an optional period
 * filter, the body, and a centred footer link.
 */
export function ChartCard({
  title,
  icon: Icon,
  filter,
  footerLink,
  children,
  className,
  bodyClassName,
}: ChartCardProps) {
  return (
    <Card className={cn('flex flex-col', className)}>
      <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-2">
        <h3 className="flex items-center gap-2 text-[15px] font-semibold text-[var(--color-heading)]">
          {Icon && <Icon className="size-4 text-[var(--color-primary)]" aria-hidden="true" />}
          {title}
        </h3>
        {filter}
      </div>

      <div className={cn('flex-1 px-5 pb-2', bodyClassName)}>{children}</div>

      {footerLink && (
        <div className="border-t border-[var(--color-border)] px-5 py-3 text-center">
          <Link
            to={footerLink.to}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-primary)] hover:underline"
          >
            {footerLink.label}
            <ArrowRight className="size-3.5 rtl-flip" aria-hidden="true" />
          </Link>
        </div>
      )}
    </Card>
  )
}
